// src/components/admin/AdminPermissions.jsx
// Admin permission management page for granting elevated privileges.
// Refined-editorial styling — cohesive with the rest of the portal.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useConfirm } from '../common/ConfirmProvider';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCheck, FiX, FiCalendar, FiTrash2, FiPlus, FiSearch,
  FiShield, FiUser, FiGlobe, FiBook, FiClock, FiAlertCircle,
} from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';

/* ============================ Layout ============================ */

const Page = styled.div`
  padding: 3rem 2.5rem 4.5rem;
  max-width: 1280px;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  background-color: var(--background-secondary);
  min-height: 100vh;

  @media (max-width: 768px) { padding: 2rem 1.5rem 3.5rem; }
  @media (max-width: 480px) { padding: 1.75rem 1rem 3rem; }
`;

/* ============================ Hero ============================ */

const Hero = styled.header`
  margin-bottom: 2.5rem;
`;

const HeroEyebrow = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.075em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const HeroTitleWrap = styled.div`
  position: relative;
  display: block;
  padding-bottom: 0.75rem;
  margin-bottom: 1rem;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 3rem;
    height: 2.5px;
    border-radius: 2px;
    background: linear-gradient(90deg, var(--primary-color) 0%, rgba(184, 149, 106, 0) 100%);
  }
`;

const HeroTitle = styled.h1`
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: clamp(2rem, 3vw + 0.8rem, 2.8rem);
  font-weight: 700;
  letter-spacing: -0.032em;
  line-height: 1.15;
  margin: 0 0 0.75rem 0;
  color: var(--text-primary);
`;

const HeroSub = styled.p`
  font-size: 1rem;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.6;
  max-width: 72ch;
  font-weight: 400;
`;

/* ============================ Layout Grid ============================ */

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled(motion.section)`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  padding: 1.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 0 0 0.5px rgba(0, 0, 0, 0.03);
  position: relative;
  overflow: hidden;

  @media (max-width: 480px) { padding: 1.25rem; }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.75rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--border-color);
`;

const SectionIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(184, 149, 106, 0.12);
  color: var(--primary-color);
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--text-primary);
  margin: 0;
`;

const SectionMeta = styled.div`
  margin-left: auto;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

/* ============================ Form ============================ */

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.625rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  line-height: 1;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 0.875rem;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  font-size: 0.9375rem;
  font-family: inherit;
  background: var(--background-primary);
  color: var(--text-primary);
  transition: all 0.18s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(184, 149, 106, 0.12);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: var(--background-secondary);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 0.875rem;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  font-size: 0.9375rem;
  font-family: inherit;
  background: var(--background-primary);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.18s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%23999' viewBox='0 0 16 16'%3e%3cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 12px;
  padding-right: 2.5rem;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(184, 149, 106, 0.12);
  }
`;

/* ============================ Searchable Select ============================ */

const SearchableSelectContainer = styled.div`
  position: relative;
`;

const SearchableSelectInput = styled.input`
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 0.875rem;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  font-size: 0.9375rem;
  font-family: inherit;
  background: var(--background-primary);
  color: var(--text-primary);
  transition: all 0.18s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(184, 149, 106, 0.12);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const DropdownList = styled(motion.div)`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  max-height: 280px;
  overflow-y: auto;
  z-index: 100;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.04);
`;

const DropdownItem = styled.div`
  padding: 0.75rem 0.875rem;
  cursor: pointer;
  transition: background 0.15s ease;
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(184, 149, 106, 0.06);
  }

  &.selected {
    background: rgba(184, 149, 106, 0.12);

    .user-name {
      color: var(--primary-color);
    }
  }

  .user-name {
    font-weight: 600;
    color: var(--text-primary);
    font-size: 0.9375rem;
    letter-spacing: -0.005em;
  }

  .user-email {
    font-size: 0.8125rem;
    color: var(--text-muted);
    margin-top: 0.1875rem;
  }
`;

const DropdownEmpty = styled.div`
  padding: 1.25rem 1rem;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.875rem;
`;

/* ============================ Checkbox Group ============================ */

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 0.5rem;
  background: var(--background-secondary);
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: var(--background-primary);
  border: 1px solid transparent;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.9375rem;
  font-weight: 400;
  line-height: 1.4;

  &:hover {
    border-color: var(--border-color);
    background: var(--background-primary);
  }

  &:has(input:checked) {
    background: rgba(184, 149, 106, 0.08);
    border-color: rgba(184, 149, 106, 0.3);
  }

  input {
    margin-top: 3px;
    cursor: pointer;
    width: 16px;
    height: 16px;
    accent-color: var(--primary-color);
    flex-shrink: 0;
  }
`;

const CheckboxLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  text-transform: none;
  letter-spacing: 0;

  .permission-name {
    font-weight: 600;
    color: var(--text-primary);
    font-size: 0.9375rem;
    letter-spacing: -0.005em;
    text-transform: none;
  }

  .permission-desc {
    font-size: 0.8125rem;
    color: var(--text-muted);
    line-height: 1.45;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
  }
`;

/* ============================ Buttons ============================ */

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.8125rem 1.5rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: inherit;
  font-weight: 600;
  font-size: 0.9375rem;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: all 0.18s cubic-bezier(0.22, 1, 0.36, 1);
  box-shadow: 0 1px 3px rgba(184, 149, 106, 0.25);
  width: 100%;
  margin-top: 0.5rem;

  &:hover:not(:disabled) {
    background: var(--primary-dark, #9a7d57);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(184, 149, 106, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

/* ============================ Grants List ============================ */

const GrantsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

const GrantItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.125rem;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  transition: all 0.18s ease;

  &:hover {
    border-color: rgba(184, 149, 106, 0.5);
    box-shadow: 0 4px 12px rgba(184, 149, 106, 0.1);
    transform: translateY(-1px);
  }
`;

const GrantIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${(p) => p.$revoked ? 'rgba(107, 114, 128, 0.12)' : p.$expired ? 'rgba(220, 38, 38, 0.1)' : 'rgba(34, 197, 94, 0.12)'};
  color: ${(p) => p.$revoked ? '#6b7280' : p.$expired ? '#dc2626' : '#22c55e'};
  flex-shrink: 0;
`;

const GrantInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const GrantName = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9375rem;
  margin-bottom: 0.25rem;
  letter-spacing: -0.005em;
`;

const GrantMeta = styled.div`
  font-size: 0.8125rem;
  color: var(--text-muted);
  display: flex;
  gap: 0.875rem;
  flex-wrap: wrap;
  line-height: 1.4;

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.3125rem 0.625rem;
  border-radius: 6px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  flex-shrink: 0;

  ${(p) => p.$revoked && `
    background: rgba(107, 114, 128, 0.12);
    color: #6b7280;
  `}

  ${(p) => p.$expired && !p.$revoked && `
    background: rgba(220, 38, 38, 0.12);
    color: #dc2626;
  `}

  ${(p) => !p.$expired && !p.$revoked && `
    background: rgba(34, 197, 94, 0.12);
    color: #16a34a;
  `}
`;

const RevokeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  background: transparent;
  color: #dc2626;
  border: 1px solid rgba(220, 38, 38, 0.2);
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(220, 38, 38, 0.08);
    border-color: rgba(220, 38, 38, 0.4);
  }
`;

/* ============================ Empty State ============================ */

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1.5rem;
  color: var(--text-muted);

  .empty-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: var(--background-secondary);
    color: var(--text-muted);
    margin-bottom: 1rem;
  }

  .empty-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 0.375rem;
  }

  .empty-desc {
    font-size: 0.8125rem;
    line-height: 1.5;
  }
`;

/* ============================ Alert ============================ */

const Alert = styled(motion.div)`
  padding: 0.875rem 1.125rem;
  border-radius: 10px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.9375rem;
  font-weight: 500;
  letter-spacing: -0.005em;

  ${(p) => p.$type === 'success' && `
    background: rgba(34, 197, 94, 0.08);
    border: 1px solid rgba(34, 197, 94, 0.25);
    color: #15803d;
  `}

  ${(p) => p.$type === 'error' && `
    background: rgba(220, 38, 38, 0.08);
    border: 1px solid rgba(220, 38, 38, 0.25);
    color: #b91c1c;
  `}
`;

/* ============================ Component ============================ */

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.32, delay, ease: [0.22, 1, 0.36, 1] },
});

function AdminPermissions() {
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);

  const [selectedUser, setSelectedUser] = useState('');
  const [userSearchInput, setUserSearchInput] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [accessibleScopes, setAccessibleScopes] = useState([]);
  const [selectedScopeValue, setSelectedScopeValue] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState(new Set());
  const [expiresAt, setExpiresAt] = useState('');

  const [userGrants, setUserGrants] = useState([]);
  const [loadingScopes, setLoadingScopes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const filteredUsers = useMemo(() => {
    if (!userSearchInput.trim()) return users;
    const search = userSearchInput.toLowerCase();
    return users.filter(user =>
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(search) ||
      user.email.toLowerCase().includes(search)
    );
  }, [users, userSearchInput]);

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4000);
  };

  const loadInitialData = useCallback(async () => {
    try {
      const [usersRes, permRes] = await Promise.allSettled([
        apiService.getUsers(),
        apiService.request('/permissions/available')
      ]);

      if (usersRes.status === 'fulfilled' && usersRes.value?.success) {
        setUsers(usersRes.value.users || []);
      }

      if (permRes.status === 'fulfilled' && permRes.value?.success) {
        setAvailablePermissions(permRes.value.permissions || []);
      }
    } catch (error) {
      logger.error('Error loading initial data', { error });
      showAlert('Failed to load data', 'error');
    }
  }, []);

  const loadUserGrants = async (userId) => {
    try {
      const data = await apiService.request(`/permissions/users/${userId}/grants`);
      if (data.success) {
        setUserGrants(data.grants || []);
      }
    } catch (error) {
      logger.error('Error loading user grants', { error });
    }
  };

  const loadAccessibleScopes = async (userId) => {
    setLoadingScopes(true);
    try {
      const data = await apiService.request(`/permissions/users/${userId}/accessible-scopes`);
      if (data.success) {
        setAccessibleScopes(data.scopes || []);
      }
    } catch (error) {
      logger.error('Error loading accessible scopes', { error });
      setAccessibleScopes([]);
    } finally {
      setLoadingScopes(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (selectedUser) {
      loadUserGrants(selectedUser);
      loadAccessibleScopes(selectedUser);
      setSelectedScopeValue('');
    } else {
      setAccessibleScopes([]);
      setSelectedScopeValue('');
    }
  }, [selectedUser]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.closest('[data-searchable-select]') === null) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handlePermissionToggle = (permissionKey) => {
    const newSet = new Set(selectedPermissions);
    if (newSet.has(permissionKey)) {
      newSet.delete(permissionKey);
    } else {
      newSet.add(permissionKey);
    }
    setSelectedPermissions(newSet);
  };

  const handleGrantPermissions = async (e) => {
    e.preventDefault();

    if (!selectedUser || !selectedScopeValue || selectedPermissions.size === 0) {
      showAlert('Please select a user, scope, and at least one permission', 'error');
      return;
    }

    let scopeType = 'global';
    let scopeId = null;
    if (selectedScopeValue !== 'global') {
      const [type, id] = selectedScopeValue.split(':');
      scopeType = type;
      scopeId = parseInt(id);
    }

    setLoading(true);
    try {
      const grantPromises = Array.from(selectedPermissions).map(permKey => {
        const payload = {
          permissionKey: permKey,
          scopeType,
        };
        if (scopeId !== null) {
          payload.scopeId = scopeId;
        }
        if (expiresAt) {
          payload.expiresAt = new Date(expiresAt).toISOString();
        }
        return apiService.request(`/permissions/users/${selectedUser}/grant`, {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      });

      const results = await Promise.all(grantPromises);
      const failed = results.filter(r => !r.success);

      if (failed.length === 0) {
        showAlert(`${selectedPermissions.size} permission${selectedPermissions.size === 1 ? '' : 's'} granted successfully`, 'success');
        setSelectedPermissions(new Set());
        setExpiresAt('');
        setSelectedScopeValue('');
        await loadUserGrants(selectedUser);
      } else {
        showAlert(`Failed to grant ${failed.length} permission(s)`, 'error');
      }
    } catch (error) {
      logger.error('Error granting permissions', { error });
      showAlert('Failed to grant permissions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokePermission = async (grantId) => {
    if (!(await confirm({ title: 'Revoke permission?', message: 'This permission grant will be revoked.', confirmText: 'Revoke', danger: true }))) {
      return;
    }

    try {
      const data = await apiService.request(`/permissions/grants/${grantId}/revoke`, {
        method: 'POST'
      });

      if (data.success) {
        showAlert('Permission revoked successfully', 'success');
        await loadUserGrants(selectedUser);
      } else {
        showAlert('Failed to revoke permission', 'error');
      }
    } catch (error) {
      logger.error('Error revoking permission', { error });
      showAlert('Failed to revoke permission', 'error');
    }
  };

  const isExpired = (expiresAt) => expiresAt && new Date(expiresAt) < new Date();
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric'
  });

  const contentPermissions = availablePermissions.filter(p =>
    p.permission_key.includes('course_content') ||
    p.permission_key.includes('course_attendance')
  );
  const selectedUserData = users.find(u => String(u.id) === selectedUser);

  const getScopeIcon = (scope) => {
    if (scope === 'global') return <FiGlobe size={14} />;
    if (scope === 'elective') return <FiBook size={14} />;
    return <FiBook size={14} />;
  };

  return (
    <Page>
      <Hero>
        <HeroEyebrow>
          <FiShield size={13} />
          Administration
        </HeroEyebrow>
        <HeroTitleWrap>
          <HeroTitle>Permissions</HeroTitle>
        </HeroTitleWrap>
        <HeroSub>
          Grant elevated privileges to instructors and staff for specific course content sections.
          Permissions can be scoped to individual courses or set globally, with optional expiration dates.
        </HeroSub>
      </Hero>

      <AnimatePresence>
        {alert && (
          <Alert
            $type={alert.type}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            {alert.type === 'success' ? <FiCheck size={16} /> : <FiAlertCircle size={16} />}
            {alert.message}
          </Alert>
        )}
      </AnimatePresence>

      <Container>
        <Section {...fadeUp(0)}>
          <SectionHeader>
            <SectionIcon>
              <FiPlus size={18} />
            </SectionIcon>
            <SectionTitle>Grant Permissions</SectionTitle>
            <SectionMeta>New Grant</SectionMeta>
          </SectionHeader>

          <FormGroup>
            <Label>
              <FiUser size={13} />
              Select User
            </Label>
            <SearchableSelectContainer data-searchable-select>
              <SearchableSelectInput
                type="text"
                placeholder="Search by name or email..."
                value={userSearchInput}
                onChange={(e) => {
                  setUserSearchInput(e.target.value);
                  setShowUserDropdown(true);
                }}
                onFocus={() => setShowUserDropdown(true)}
              />
              <SearchIcon>
                <FiSearch size={16} />
              </SearchIcon>
              <AnimatePresence>
                {showUserDropdown && (
                  <DropdownList
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.15 }}
                  >
                    {filteredUsers.length === 0 ? (
                      <DropdownEmpty>No users found</DropdownEmpty>
                    ) : (
                      filteredUsers.map(user => (
                        <DropdownItem
                          key={user.id}
                          className={selectedUser === String(user.id) ? 'selected' : ''}
                          onClick={() => {
                            setSelectedUser(String(user.id));
                            setUserSearchInput(`${user.first_name} ${user.last_name}`);
                            setShowUserDropdown(false);
                          }}
                        >
                          <div className="user-name">{user.first_name} {user.last_name}</div>
                          <div className="user-email">{user.email}</div>
                        </DropdownItem>
                      ))
                    )}
                  </DropdownList>
                )}
              </AnimatePresence>
            </SearchableSelectContainer>
          </FormGroup>

          <FormGroup>
            <Label>
              <FiBook size={13} />
              Scope
            </Label>
            <Select
              value={selectedScopeValue}
              onChange={(e) => setSelectedScopeValue(e.target.value)}
              disabled={!selectedUser || loadingScopes}
            >
              <option value="">
                {!selectedUser
                  ? '— Select a user first —'
                  : loadingScopes
                    ? 'Loading…'
                    : '— Choose a scope —'}
              </option>
              {selectedUser && !loadingScopes && (
                <>
                  <option value="global">Global — All courses & electives</option>
                  {accessibleScopes.filter(s => s.scope_type === 'course').length > 0 && (
                    <optgroup label="Courses">
                      {accessibleScopes.filter(s => s.scope_type === 'course').map(scope => (
                        <option key={`course-${scope.id}`} value={`course:${scope.id}`}>
                          {scope.name}{scope.code ? ` (${scope.code})` : ''} — {scope.relationship}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {accessibleScopes.filter(s => s.scope_type === 'elective').length > 0 && (
                    <optgroup label="Electives">
                      {accessibleScopes.filter(s => s.scope_type === 'elective').map(scope => (
                        <option key={`elective-${scope.id}`} value={`elective:${scope.id}`}>
                          {scope.name}{scope.code ? ` (${scope.code})` : ''} — {scope.relationship}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </>
              )}
            </Select>
            {selectedUser && !loadingScopes && accessibleScopes.length === 0 && (
              <div style={{
                marginTop: '0.5rem',
                fontSize: '0.8125rem',
                color: 'var(--text-muted)',
                fontStyle: 'italic'
              }}>
                This user has no courses or electives. Only global scope is available.
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <Label>
              <FiShield size={13} />
              Permissions to Grant
            </Label>
            {contentPermissions.length > 0 ? (
              <CheckboxGroup>
                {contentPermissions.map(perm => (
                  <CheckboxItem key={perm.id}>
                    <input
                      type="checkbox"
                      checked={selectedPermissions.has(perm.permission_key)}
                      onChange={() => handlePermissionToggle(perm.permission_key)}
                    />
                    <CheckboxLabel>
                      <span className="permission-name">{perm.name}</span>
                      {perm.description && (
                        <span className="permission-desc">{perm.description}</span>
                      )}
                    </CheckboxLabel>
                  </CheckboxItem>
                ))}
              </CheckboxGroup>
            ) : (
              <EmptyState>
                <div className="empty-icon-wrap">
                  <FiShield size={24} />
                </div>
                <div className="empty-title">No permissions available</div>
                <div className="empty-desc">Permission definitions have not been seeded</div>
              </EmptyState>
            )}
          </FormGroup>

          <FormGroup>
            <Label>
              <FiClock size={13} />
              Expiration Date <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: '0.25rem' }}>(optional)</span>
            </Label>
            <Input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </FormGroup>

          <Button
            onClick={handleGrantPermissions}
            disabled={loading || !selectedUser || !selectedScopeValue || selectedPermissions.size === 0}
          >
            <FiPlus size={16} />
            {loading ? 'Granting…' : `Grant ${selectedPermissions.size > 0 ? `${selectedPermissions.size} ` : ''}Permission${selectedPermissions.size === 1 ? '' : 's'}`}
          </Button>
        </Section>

        <Section {...fadeUp(0.08)}>
          <SectionHeader>
            <SectionIcon>
              <FiShield size={18} />
            </SectionIcon>
            <SectionTitle>Current Grants</SectionTitle>
            <SectionMeta>
              {selectedUserData
                ? `${userGrants.filter(g => !g.revoked_at).length} Active`
                : 'Select a user'}
            </SectionMeta>
          </SectionHeader>

          {!selectedUser ? (
            <EmptyState>
              <div className="empty-icon-wrap">
                <FiUser size={24} />
              </div>
              <div className="empty-title">No user selected</div>
              <div className="empty-desc">Search and select a user to view their permission grants</div>
            </EmptyState>
          ) : userGrants.length === 0 ? (
            <EmptyState>
              <div className="empty-icon-wrap">
                <FiShield size={24} />
              </div>
              <div className="empty-title">No permissions granted</div>
              <div className="empty-desc">
                {selectedUserData
                  ? `${selectedUserData.first_name} doesn't have any permission grants yet`
                  : 'No grants found'}
              </div>
            </EmptyState>
          ) : (
            <GrantsList>
              <AnimatePresence>
                {userGrants.map((grant, i) => {
                  const expired = isExpired(grant.expires_at);
                  const revoked = !!grant.revoked_at;

                  return (
                    <GrantItem
                      key={grant.id}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 8 }}
                      transition={{ duration: 0.2, delay: 0 }}
                    >
                      <GrantIcon $revoked={revoked} $expired={expired}>
                        <FiShield size={16} />
                      </GrantIcon>
                      <GrantInfo>
                        <GrantName>{grant.permission_name}</GrantName>
                        <GrantMeta>
                          <span>
                            {getScopeIcon(grant.scope_type)}
                            {(() => {
                              if (grant.scope_type === 'global') return 'Global';
                              if (grant.scope_name) {
                                const typeLabel = grant.scope_type === 'course' ? 'Course' : 'Elective';
                                return `${typeLabel}: ${grant.scope_name}${grant.scope_code ? ` (${grant.scope_code})` : ''}`;
                              }
                              return `${grant.scope_type.charAt(0).toUpperCase() + grant.scope_type.slice(1)} #${grant.scope_id}`;
                            })()}
                          </span>
                          {grant.expires_at && !revoked && (
                            <span>
                              <FiCalendar size={12} />
                              {expired ? 'Expired' : 'Expires'} {formatDate(grant.expires_at)}
                            </span>
                          )}
                          {revoked && (
                            <span>
                              <FiX size={12} />
                              Revoked {formatDate(grant.revoked_at)}
                            </span>
                          )}
                        </GrantMeta>
                      </GrantInfo>
                      <StatusBadge $revoked={revoked} $expired={expired}>
                        {revoked ? 'Revoked' : expired ? 'Expired' : 'Active'}
                      </StatusBadge>
                      {!revoked && (
                        <RevokeButton onClick={() => handleRevokePermission(grant.id)}>
                          <FiTrash2 size={13} />
                          Revoke
                        </RevokeButton>
                      )}
                    </GrantItem>
                  );
                })}
              </AnimatePresence>
            </GrantsList>
          )}
        </Section>
      </Container>
    </Page>
  );
}

export default AdminPermissions;
